// Thin wrapper around the shared @mixgoat/share-image package — just supplies
// the asset URLs that webpack resolves from the app's bundled assets.
import {
    generateShareImage as _generate,
    generateShareImagePages as _generatePages,
    shareImages as _shareMany,
    pageFileName,
} from '@mixgoat/share-image';
import bgImage from '../assets/black_background_2.png';
import blueCheckImage from '../assets/blue_check.svg';

export async function generateShareImage(data) {
    return _generate(data, { bgUrl: bgImage, blueCheckUrl: blueCheckImage });
}

// Set Planner: one 9:16 PNG per page (see the package's
// generateShareImagePages) — always an array, single-page sets included.
export async function generateShareImagePages(data) {
    return _generatePages(data, { bgUrl: bgImage, blueCheckUrl: blueCheckImage });
}

// The shared package's shareImage() shares the PNG via the Web Share API
// (navigator.share({ files })). Android's Capacitor WebView has no
// navigator.share at all (silently falls back to an <a download>, no sheet),
// and iOS WKWebView's Web Share file support is historically unreliable. So on
// EVERY native platform we route the image share through the Capacitor Share
// plugin instead: persist the PNG to the cache dir (@capacitor/filesystem) and
// share its file:// URI — a deterministic native share sheet on both iOS
// (UIActivityViewController) and Android. Desktop/web (no Capacitor global) are
// completely unaffected and keep the original navigator.share/download path —
// the plugins are read off window.Capacitor at runtime so webpack never bundles
// them into the desktop/web build.
export const shareImage = async (blob, defaultName) => shareImages([blob], defaultName);

// Multi-page variant (Set Planner). Native: every page is written to the
// cache dir and ONE Capacitor share sheet carries all of them (Instagram
// picks them up as a carousel). Desktop/web: the package's shareImages
// (save dialog → <name>_N.png siblings / Web Share / sequential downloads).
export const shareImages = async (blobs, defaultName) => {
    if (!blobs || blobs.length === 0) return { method: 'cancelled' };
    const cap = typeof window !== 'undefined' ? window.Capacitor : undefined;
    const isNative = !!(cap && cap.isNativePlatform && cap.isNativePlatform());
    const Share = cap && cap.Plugins && cap.Plugins.Share;
    const Filesystem = cap && cap.Plugins && cap.Plugins.Filesystem;

    // iOS: our MixGoatShare plugin hands the sheet real UIImages, so Photos'
    // "Save Image" (and Instagram) appear — a file:// URL via @capacitor/share
    // only ever offered "Save to Files". Android keeps the file-URI path
    // (its sheet already lists gallery/photo apps for image files).
    const MixGoatShare = cap && cap.Plugins && cap.Plugins.MixGoatShare;
    const platform = cap && typeof cap.getPlatform === 'function' ? cap.getPlatform() : '';
    if (isNative && platform === 'ios' && MixGoatShare && typeof MixGoatShare.shareImages === 'function') {
        try {
            const images = [];
            for (const b of blobs) images.push(await blobToBase64(b));
            const res = await MixGoatShare.shareImages({ images, title: '' });
            return { method: res && res.method === 'cancelled' ? 'cancelled' : 'shared' };
        } catch (err) {
            if (err && /cancel/i.test(err.message || '')) return { method: 'cancelled' };
            // Fall through to the file-URI share below.
        }
    }

    if (isNative && Share && Filesystem) {
        try {
            const base = (defaultName || 'mixgoat_share.png').replace(/[^a-zA-Z0-9._-]/g, '_');
            const uris = [];
            for (let i = 0; i < blobs.length; i++) {
                const base64 = await blobToBase64(blobs[i]);
                const name = pageFileName(base, i, blobs.length);
                // Directory.Cache === 'CACHE'; pass the literal so we don't have to
                // statically import the enum (which would pull the plugin into the
                // desktop/web bundle).
                const written = await Filesystem.writeFile({ path: name, data: base64, directory: 'CACHE' });
                if (written && written.uri) uris.push(written.uri);
            }
            if (uris.length !== blobs.length) return _shareMany(blobs, defaultName);
            await Share.share({ title: 'MixGoat', files: uris });
            return { method: 'shared' };
        } catch (err) {
            if (err && /cancel/i.test(err.message || '')) return { method: 'cancelled' };
            // Genuine failure — fall back to the web/download path rather than
            // leaving the user with nothing.
            return _shareMany(blobs, defaultName);
        }
    }

    return _shareMany(blobs, defaultName);
};

function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result).split(',')[1] || '');
        reader.onerror = () => reject(reader.error || new Error('blob read failed'));
        reader.readAsDataURL(blob);
    });
}
